<template>
  <div class="notes-container">
    <NoteBubble
      v-for="note in notes"
      :id="id"
      :key="note.noteId"
      :name="`note-${note.noteId}`"
      :note="note"
      :data-key="dataKey"
      @delete="remove"
      @edit-start="onEditStart"
      @edit-end="onEditEnd"
    />
    <NoteBubble
      v-if="!isAnyEditing"
      :id="`new-${dataKey}-${id}`"
      :key="`new-note-${dataKey}-${id}`"
      v-model:draftText="draftText"
      :data-key="dataKey"
      :note="newNote"
      name="note-draft"
      draft
      @draft-save="createDraft"
      @draft-blur="createDraft"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { useNotesStore } from '@/stores/localNotes';

import NoteBubble from './NoteBubble.vue';

const props = defineProps<{ dataKey: string; id: string | number }>();
const store = useNotesStore();

const emit = defineEmits<{
  (e: 'update:count', value: number): void;
}>();

const notes = computed(() => store.getNotes(props.dataKey, props.id) ?? []);
const newNote = computed(() => ({
  noteId: -1,
  text: '',
  createdAt: new Date().toISOString(),
}));

const draftText = ref('');

const editingIds = ref<Set<number>>(new Set());
function onEditStart(id: number) {
  editingIds.value.add(id);
}
function onEditEnd(id: number) {
  editingIds.value.delete(id);
}
const isAnyEditing = computed(() => editingIds.value.size > 0);

function createDraft() {
  const text = draftText.value.trim();
  if (!text) return;
  store.addNote(props.dataKey, props.id, text);
  draftText.value = '';
}

function remove(noteId: number) {
  store.deleteNote(props.dataKey, props.id, noteId);
}

watch(
  () => notes.value.length,
  (n) => emit('update:count', n),
  { immediate: true },
);
</script>

<style scoped lang="css">
.notes-container {
  display: flex;
  flex-direction: column;
}
</style>
